import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { ColDef } from "ag-grid-community";
import { useNavigate, useParams } from "react-router-dom";
import { LoadingState, InputNumber, AgGridTable } from "components";
import { useCustomer, useOtherOrder } from "hooks";
import { OtherOrder, OtherOrderForm, OtherOrderItem } from "types";
import { ROUTES } from "utils";
import SnackbarUtils from "utils/SnackbarUtils";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { useForm } from "react-hook-form";
import {
  normalizeDateInput,
  parseNumberSafe,
} from "utils/helpers/accounting";
import { sanitizeOtherOrderItems } from "utils/helpers/sanitizers";

const todayString = () => new Date().toISOString().slice(0, 10);

type OtherOrderItemWithMeta = OtherOrderItem & {
  __tempId?: string;
  __actions?: any;
};

const defaultItem: OtherOrderItemWithMeta = {
  operation_name: "",
  amount: 0,
  vat_rate: undefined,
  vat_reduce_rate: undefined,
  pit_rate: undefined,
  object_id: undefined,
  object_code: "",
  object_name: "",
  reason: "",
  description: "",
};

const defaultFormValues: OtherOrderForm = {
  order_code: "",
  order_date: todayString(),
  description: "",
  items: [],
};


const ensureTempIds = (
  list: OtherOrderItemWithMeta[],
): OtherOrderItemWithMeta[] =>
  list.map((item) =>
    item.__tempId
      ? item
      : { ...item, __tempId: `temp-${Math.random().toString(36).slice(2, 9)}` },
  );

const createEmptyItem = (): OtherOrderItemWithMeta => ({
  ...defaultItem,
  __tempId: `temp-${Math.random().toString(36).slice(2, 9)}`,
});

const OtherOrderDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const {
    register,
    handleSubmit: formHandleSubmit,
    reset,
    watch,
  } = useForm<OtherOrderForm>({
    defaultValues: defaultFormValues,
  });

  const { fetchDetail, createOtherOrder, updateOtherOrder } = useOtherOrder();
  const { data: customersData } = useCustomer().fetchList(
    { page: 1, pageSize: 200 },
    true,
  );

  const customerMap = useMemo(() => {
    const map = new Map<number | string, any>();
    (customersData?.data as any[])?.forEach((c) => {
      map.set(c.id, c);
      map.set(String(c.id), c);
    });
    return map;
  }, [customersData]);

  const { data: detailResponse, isFetching: isFetchingDetail } = fetchDetail(
    id ?? "",
    isEdit,
  );

  const [items, setItems] = useState<OtherOrderItemWithMeta[]>([
    createEmptyItem(),
  ]);

  useEffect(() => {
    if (!detailResponse?.data) return;
    const order = detailResponse.data as OtherOrder;
    const sanitizedItems = sanitizeOtherOrderItems(
      order.items,
      customerMap,
    ) as OtherOrderItemWithMeta[];
    reset({
      order_code: order.order_code ?? "",
      order_date:
        normalizeDateInput(order.order_date) ||
        normalizeDateInput((order as any).accounting_date) ||
        todayString(),
      description: order.description ?? "",
      items: sanitizedItems,
    });
    setItems(
      sanitizedItems.length
        ? ensureTempIds(sanitizedItems)
        : [createEmptyItem()],
    );
  }, [detailResponse, customerMap, reset]);

  useEffect(() => {
    if (isEdit) return;
    const currentDate = watch("order_date");
    const normalized =
      (currentDate && normalizeDateInput(currentDate)) || todayString();
    reset({ ...defaultFormValues, order_date: normalized });
    setItems([createEmptyItem()]);
  }, [isEdit, reset, watch]);

  const totalAmount = useMemo(
    () =>
      items.reduce(
        (sum, item) => sum + parseNumberSafe(item.amount ?? 0),
        0,
      ),
    [items],
  );

  const handleAddItem = useCallback(() => {
    setItems((prev) => [...prev, createEmptyItem()]);
  }, []);

  const handleItemChange = useCallback(
    (index: number, key: keyof OtherOrderItem, value: any) => {
      setItems((prev) =>
        prev.map((item, idx) => {
          if (idx !== index) return item;

          if (
            key === "amount" ||
            key === "vat_rate" ||
            key === "vat_reduce_rate" ||
            key === "pit_rate"
          ) {
            return { ...item, [key]: parseNumberSafe(value ?? 0) };
          }

          if (key === "object_id") {
            const normalized =
              value === "" || value === null || value === undefined
                ? undefined
                : Number.isNaN(Number(value))
                  ? String(value).trim()
                  : Number(value);
            if (normalized === undefined) {
              return {
                ...item,
                object_id: undefined,
                object_code: "",
                object_name: "",
              };
            }
            const matched =
              customerMap.get(normalized) ||
              customerMap.get(String(normalized));
            return {
              ...item,
              object_id: normalized as any,
              object_code: matched?.code ?? "",
              object_name: matched?.name ?? "",
            };
          }

          return { ...item, [key]: value };
        }),
      );
    },
    [customerMap],
  );

  const handleRemoveItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  const buildPayload = useCallback(
    (data: OtherOrderForm): OtherOrderForm | null => {
      if (!data.order_date) {
        SnackbarUtils.error("Ngày chứng từ là bắt buộc");
        return null;
      }

      const normalizedItems = sanitizeOtherOrderItems(
        items,
        customerMap,
      ) as OtherOrderItemWithMeta[];

      if (
        !normalizedItems.length ||
        !normalizedItems.some((item) => parseNumberSafe(item.amount) > 0)
      ) {
        SnackbarUtils.error(
          "Vui lòng nhập ít nhất một dòng nghiệp vụ với số tiền hợp lệ",
        );
        return null;
      }

      const invalidItem = normalizedItems.find(
        (item) =>
          !item.operation_name || parseNumberSafe(item.amount ?? 0) <= 0,
      );
      if (invalidItem) {
        SnackbarUtils.error(
          "Mỗi nghiệp vụ cần có tên nghiệp vụ và số tiền hợp lệ",
        );
        return null;
      }

      return {
        ...data,
        order_code: data.order_code?.trim() || undefined,
        description: data.description?.trim() || undefined,
        items: normalizedItems,
      };
    },
    [customerMap, items],
  );

  const onSubmit = useCallback(
    (formData: OtherOrderForm) => {
      const payload = buildPayload(formData);
      if (!payload) return;

      if (isEdit && id) {
        updateOtherOrder.mutate(
          { id: +id, ...payload },
          {
            onSuccess: () => {
              navigate(ROUTES.ACCOUNTING.OTHER_DOCUMENT);
            },
          },
        );
        return;
      }

      createOtherOrder.mutate(payload, {
        onSuccess: () => {
          navigate(ROUTES.ACCOUNTING.OTHER_DOCUMENT);
        },
      });
    },
    [buildPayload, createOtherOrder, id, isEdit, navigate, updateOtherOrder],
  );

  const isSaving = createOtherOrder.isPending || updateOtherOrder.isPending;
  const formSubmit = formHandleSubmit(onSubmit);

  const customerOptions = useMemo(
    () =>
      ((customersData?.data as any[]) ?? []).map((c) => ({
        value: c.id,
        label: `${c.code}${c.name ? ` - ${c.name}` : ""}`,
      })),
    [customersData],
  );

  const suppressGridKeyboard = useCallback((params: any) => {
    const event = params?.event as KeyboardEvent | undefined;
    if (!event) return false;
    const target = event.target as HTMLElement | null;
    if (!target) return false;
    const tag = target.tagName;
    if (
      (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") &&
      ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)
    ) {
      event.stopPropagation?.();
      return true;
    }
    return false;
  }, []);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      filter: false,
      floatingFilter: false,
      resizable: true,
      editable: false,
      minWidth: 120,
      cellClass: "!border-transparent",
      suppressKeyboardEvent: suppressGridKeyboard,
    }),
    [suppressGridKeyboard],
  );

  const columns = useMemo<
    ColDef<OtherOrderItemWithMeta & { __actions?: any }>[]
  >(() => {
    const renderNumberInput = (
      params: any,
      key: keyof OtherOrderItem,
      opts?: { suffix?: string; decimalScale?: number },
    ) => {
      const rowIndex = params.node?.rowIndex ?? 0;
      return (
        <InputNumber
          value={parseNumberSafe((params.data as OtherOrderItem)?.[key])}
          onValueChange={(values) =>
            handleItemChange(rowIndex, key, values.floatValue ?? 0)
          }
          className="h-9 w-full px-2 py-1 text-right text-sm"
          suffix={opts?.suffix}
          decimalScale={opts?.decimalScale ?? 0}
        />
      );
    };

    return [
      {
        headerName: "Nghiệp vụ *",
        field: "operation_name",
        minWidth: 200,
        flex: 1,
        pinned: "left",
        cellRenderer: (params) => {
          const rowIndex = params.node?.rowIndex ?? 0;
          return (
            <input
              value={(params.data as OtherOrderItem)?.operation_name ?? ""}
              onChange={(e) =>
                handleItemChange(rowIndex, "operation_name", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Thu tiền dịch vụ"
            />
          );
        },
      },
      {
        headerName: "Đối tượng",
        field: "object_id",
        minWidth: 200,
        flex: 1,
        cellRenderer: (params) => {
          const rowIndex = params.node?.rowIndex ?? 0;
          return (
            <select
              value={(params.data as OtherOrderItem)?.object_id ?? ""}
              onChange={(e) =>
                handleItemChange(rowIndex, "object_id", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="">Chọn đối tượng</option>
              {customerOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          );
        },
      },
      {
        headerName: "Mã đối tượng",
        field: "object_code",
        minWidth: 160,
        flex: 0.9,
        cellRenderer: (params) => {
          const rowIndex = params.node?.rowIndex ?? 0;
          return (
            <input
              value={(params.data as OtherOrderItem)?.object_code ?? ""}
              onChange={(e) =>
                handleItemChange(rowIndex, "object_code", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="KH0001"
            />
          );
        },
      },
      {
        headerName: "Tên đối tượng",
        field: "object_name",
        minWidth: 200,
        flex: 1,
        cellRenderer: (params) => {
          const rowIndex = params.node?.rowIndex ?? 0;
          return (
            <input
              value={(params.data as OtherOrderItem)?.object_name ?? ""}
              onChange={(e) =>
                handleItemChange(rowIndex, "object_name", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Khách lẻ"
            />
          );
        },
      },
      {
        headerName: "Số tiền *",
        field: "amount",
        minWidth: 160,
        flex: 1,
        cellRenderer: (params) =>
          renderNumberInput(params, "amount", {
            suffix: " ₫",
            decimalScale: 0,
          }),
        cellClass: "text-right",
      },
      {
        headerName: "VAT (%)",
        field: "vat_rate",
        minWidth: 140,
        flex: 0.8,
        cellRenderer: (params) =>
          renderNumberInput(params, "vat_rate", {
            decimalScale: 2,
            suffix: "%",
          }),
        cellClass: "text-right",
      },
      {
        headerName: "Giảm VAT (%)",
        field: "vat_reduce_rate",
        minWidth: 150,
        flex: 0.9,
        cellRenderer: (params) =>
          renderNumberInput(params, "vat_reduce_rate", {
            decimalScale: 2,
            suffix: "%",
          }),
        cellClass: "text-right",
      },
      {
        headerName: "PIT (%)",
        field: "pit_rate",
        minWidth: 120,
        flex: 0.8,
        cellRenderer: (params) =>
          renderNumberInput(params, "pit_rate", {
            decimalScale: 2,
            suffix: "%",
          }),
        cellClass: "text-right",
      },
      {
        headerName: "Lý do",
        field: "reason",
        minWidth: 200,
        flex: 1,
        cellRenderer: (params) => {
          const rowIndex = params.node?.rowIndex ?? 0;
          return (
            <input
              value={(params.data as OtherOrderItem)?.reason ?? ""}
              onChange={(e) =>
                handleItemChange(rowIndex, "reason", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Nộp tiền thuế"
            />
          );
        },
      },
      {
        headerName: "Mô tả",
        field: "description",
        minWidth: 200,
        flex: 1,
        cellRenderer: (params) => {
          const rowIndex = params.node?.rowIndex ?? 0;
          return (
            <input
              value={(params.data as OtherOrderItem)?.description ?? ""}
              onChange={(e) =>
                handleItemChange(rowIndex, "description", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Thu tiền sản phẩm"
            />
          );
        },
      },
      {
        headerName: "Thao tác",
        field: "__actions",
        width: 130,
        editable: false,
        cellRenderer: (params) => {
          const rowIndex = params.node?.rowIndex ?? 0;
          return (
            <div className="flex items-center justify-center h-full">
              <button
                type="button"
                onClick={() => handleRemoveItem(rowIndex)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-60"
                disabled={items.length === 1}
              >
                <Trash2 className="h-4 w-4" />
                Xóa
              </button>
            </div>
          );
        },
      },
    ];
  }, [customerOptions, handleItemChange, handleRemoveItem]);

  const getRowId = useCallback((params: any) => {
    const data = params.data as OtherOrderItemWithMeta;
    if (data?.__tempId) return data.__tempId;
    return String(params.rowIndex ?? params.node?.rowIndex ?? Math.random());
  }, []);

  return (
    <>
      {(isFetchingDetail || isSaving) && (
        <LoadingState
          message={isEdit ? "Đang tải chứng từ" : "Đang lưu chứng từ"}
          description="Vui lòng chờ trong giây lát."
        />
      )}
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 uppercase">Chứng từ khác</p>
            <h1 className="text-2xl font-semibold text-gray-900">
              {isEdit ? "Cập nhật chứng từ khác" : "Thêm chứng từ khác"}
            </h1>
            <p className="text-sm text-gray-600">
              Thông tin theo mẫu: ngày chứng từ, mô tả và danh sách nghiệp vụ.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(ROUTES.ACCOUNTING.OTHER_DOCUMENT)}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Danh sách chứng từ
            </button>
            <button
              type="button"
              onClick={formSubmit}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-70"
              disabled={isSaving}
            >
              <Save className="h-4 w-4" />
              {isSaving ? "Đang lưu..." : "Lưu chứng từ"}
            </button>
          </div>
        </div>

        <form onSubmit={formSubmit} className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-4 py-3">
              <h2 className="text-base font-semibold text-gray-900">
                Thông tin chung
              </h2>
            </div>
            <div className="grid gap-4 px-4 py-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Số chứng từ (tự sinh nếu để trống)
                </label>
                <input
                  {...register("order_code")}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="CTK00001"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Ngày chứng từ *
                </label>
                <input
                  type="date"
                  {...register("order_date", { required: true })}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium text-gray-700">
                  Mô tả
                </label>
                <textarea
                  {...register("description")}
                  rows={2}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Ví dụ: Thu tiền sản phẩm, nộp tiền thuế..."
                />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Danh sách nghiệp vụ
                </h2>
                <p className="text-xs text-gray-500">
                  Mỗi dòng gồm nghiệp vụ, đối tượng, số tiền, VAT, giảm VAT, PIT
                  và lý do.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-xs text-gray-500">Tổng tiền</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {totalAmount.toLocaleString("vi-VN")} ₫
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Plus className="h-4 w-4" />
                  Thêm dòng
                </button>
              </div>
            </div>
            <div className="px-2 py-3">
              <AgGridTable
                rows={items}
                columns={columns}
                pagination={false}
                defaultColDef={defaultColDef}
                pinnedLeftKeys={["operation_name"]}
                immutableData
                deltaRowDataMode
                getRowId={getRowId}
                suppressCellFocus
              />
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default OtherOrderDetail;
